import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { checkApiKey } from "../../actions";
import { brand, notice } from "../../theme/businessFlowsTheme";
import { MethodChip, Notice, SAMPLE_KEY, mono, statusTone, type } from "./devUi";

// How the databases a key opens are read back to its owner.
const names = (databases) =>
  (databases || [])
    .map((database) => database?.label || database?.name)
    .filter(Boolean)
    .join(", ");

const worksMessage = (databases) => {
  const count = (databases || []).length;
  const list = names(databases);
  if (count === 0) return "This key works. It opens no database yet - ask bpExperts to add one to your workspace.";
  return `This key works. It opens ${count} database${count === 1 ? "" : "s"}${list ? `: ${list}` : ""}.`;
};

// What the reader is told, for every answer the gate in front of /api/v1 can
// give. The server's own words are shown underneath; these are the headline
// and the next step, so nobody has to work out what a number means.
const VERDICTS = {
  401: {
    title: "This key isn't valid",
    next: "A key is shown once, so one that was retyped or cut short won't work. Paste it whole, or create a fresh one.",
  },
  402: {
    title: "This key has used this month's requests",
    next: "It works again at the start of next month. A larger plan raises the monthly allowance.",
  },
  403: {
    title: "This workspace's plan doesn't include the API",
    next: "Nothing is wrong with the key itself. Ask bpExperts to add API access to the plan.",
  },
  404: {
    title: "That isn't a path this API serves",
    next: "Check the address against the reference below.",
  },
  429: {
    title: "Too many requests in one minute",
    next: "Wait a moment and check again. Every refusal like this carries Retry-After, in seconds.",
  },
  503: {
    title: "The database is unreachable right now",
    next: "The key is fine. Try the same check again shortly.",
  },
};

// A check can end without the server ever answering, and those endings are
// not all the same thing. Reading them as one - "we couldn't reach the API" -
// is what once blamed the connection for a key that had a line break in it.
const REASONS = {
  empty: {
    tone: notice.quiet,
    title: "Paste your key first",
    next: "It's the long string beginning bfb_live_, shown once when the key was created.",
  },
  malformed: {
    tone: notice.fix,
    title: "This key isn't valid",
    next: "Nothing was sent, so no request was spent on it. Select the key from end to end, copy it again, and paste it here.",
  },
  network: {
    tone: notice.quiet,
    title: "We couldn't reach the API",
    next: "Check your connection and try again. Nothing was sent, and the key is still only in this page.",
  },
};

const verdictOf = (result) => {
  if (result?.ok) return { ok: true, status: 200, tone: notice.good, title: "This key works", next: "" };
  const status = Number(result?.status || 0);
  if (status) {
    const known = VERDICTS[status] || {
      title: "The server refused this key",
      next: "What each refusal means is in “When a call is refused” below.",
    };
    return { ok: false, status, tone: statusTone(status), ...known };
  }
  // No status: the check ended in this browser. `reason` says how; an older
  // answer without one can only have been the network.
  return { ok: false, status: 0, ...(REASONS[result?.reason] || REASONS.network) };
};

const iconFor = (verdict) => {
  if (verdict.ok) return <CheckCircleRoundedIcon fontSize="small" />;
  if (verdict.tone === notice.wait) return <ScheduleRoundedIcon fontSize="small" />;
  if (verdict.tone === notice.fix) return <ErrorOutlineRoundedIcon fontSize="small" />;
  return <InfoOutlinedIcon fontSize="small" />;
};

// Swagger's own Authorize box only remembers what was typed, so a key with a
// typo in it still reads as "Authorized" - which is what made a wrong key look
// like a successful login. This box answers the real question instead: it
// spends one call on GET /api/v1/databases and shows what the server said.
// Only a key the server accepted is handed to the try-it boxes, and the key is
// held in this page's memory alone: never in localStorage, never in
// sessionStorage, gone when the tab is closed or "Forget key" is pressed.
//
// It sits on paper, not on the portal's dark material: checking a key is
// something the reader does, not something they copy, and a form the size of
// this one in navy made the top of the page read as a console.
export default function ApiKeyBox({ onVerified, onForget }) {
  const [key, setKey] = useState("");
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const verdict = result ? verdictOf(result) : null;

  const clear = () => {
    setKey("");
    setResult(null);
    setShown(false);
    if (onForget) onForget();
  };

  const check = async (event) => {
    if (event) event.preventDefault();
    if (busy) return;
    setBusy(true);
    setResult(null);
    if (onForget) onForget();
    const answer = await checkApiKey(key);
    setBusy(false);
    setResult(answer);
    if (answer?.ok && onVerified) onVerified(String(key).trim(), answer.databases || []);
  };

  return (
    <Box sx={{ borderRadius: "18px", overflow: "hidden", bgcolor: "#fff", border: `1px solid ${brand.line}` }}>
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ px: { xs: 2, sm: 2.75 }, py: 1.25, bgcolor: brand.canvas, borderBottom: `1px solid ${brand.line}` }}
      >
        <MethodChip method="GET" />
        <Typography
          component="span"
          sx={{ fontFamily: `${mono} !important`, fontSize: "0.78rem", color: brand.inkSoft, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
        >
          /api/v1/databases
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography component="span" sx={{ ...type.meta, display: { xs: "none", sm: "block" } }}>
          the smallest real call there is
        </Typography>
      </Stack>

      <Box sx={{ px: { xs: 2, sm: 3.5 }, py: { xs: 2.5, sm: 3.5 } }}>
        <Typography variant="h3" component="h3" sx={{ fontSize: "1.3rem", fontWeight: 700, letterSpacing: "-0.025em", color: brand.ink }}>
          Check your key
        </Typography>
        <Typography sx={{ ...type.prose, fontSize: "0.94rem", mt: 1, mb: 3 }}>
          Paste a key and we spend one real request on it. A key the server accepts fills in the examples below and signs the try-it boxes in the
          reference, so you never type it twice.
        </Typography>

        <Box component="form" onSubmit={check} noValidate>
          <Typography
            component="label"
            htmlFor="developer-api-key"
            sx={{ display: "block", fontSize: "0.84rem", fontWeight: 650, color: brand.ink, mb: 1 }}
          >
            Your API key
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "flex-start" }}>
            <TextField
              id="developer-api-key"
              placeholder={SAMPLE_KEY}
              value={key}
              onChange={(event) => setKey(event.target.value)}
              type={shown ? "text" : "password"}
              autoComplete="off"
              spellCheck={false}
              inputProps={{ style: { fontFamily: mono, fontSize: "0.88rem" } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={shown ? "Hide the key" : "Show the key"}>
                      <IconButton
                        onClick={() => setShown((was) => !was)}
                        edge="end"
                        size="small"
                        aria-label={shown ? "Hide the key" : "Show the key"}
                      >
                        {shown ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <LoadingButton type="submit" variant="contained" loading={busy} sx={{ minHeight: 51, px: 3.5 }}>
                Check
              </LoadingButton>
              <Button
                type="button"
                variant="text"
                onClick={clear}
                disabled={busy || (!key && !result)}
                sx={{ minHeight: 51, px: 2, color: brand.muted, "&:hover": { color: brand.ink, bgcolor: brand.shell } }}
              >
                Forget key
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box role="status" aria-live="polite" sx={{ mt: verdict ? 2.5 : 0 }}>
          {verdict && (
            <Notice
              tone={verdict.tone}
              icon={iconFor(verdict)}
              title={verdict.title}
              next={verdict.ok ? "The try-it boxes in the reference below now send it for you." : `${verdict.next}${verdict.status > 0 ? ` The server answered ${verdict.status}.` : ""}`}
            >
              {verdict.ok ? worksMessage(result.databases) : result.msg}
            </Notice>
          )}
        </Box>

        <Typography sx={{ ...type.meta, mt: 2.5, maxWidth: 560 }}>
          The key stays in this page only. It is never written to your browser's storage, and closing the tab forgets it.
        </Typography>
      </Box>
    </Box>
  );
}
